import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import type { ElementType } from "react";
import { ButtonBase, Stack, Typography, styled } from "~/common/mui";
import { ControlOptionLabel } from "./ControlOptionLabel";
import type { ControlOptionViewModel } from "./controlViewModel";

export interface TileOptionProps {
  option: ControlOptionViewModel;
  selected: boolean;
  hasError: boolean;
  disabled: boolean;
  layout: "fill" | "compact";
  standardLabel?: string;
  onSelect: (value: string) => void;
}

export const TileOptionButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "hasError",
})<{ selected: boolean; hasError: boolean; component?: ElementType }>(({ selected, hasError, theme }) => ({
  position: "relative",
  minWidth: 0,
  width: "100%",
  height: "100%",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  gap: theme.spacing(1),
  overflow: "hidden",
  textAlign: "left",
  borderRadius: theme.spacing(2),
  border: 0,
  boxShadow: hasError
    ? `inset 0 0 0 ${theme.spacing(0.25)} ${theme.palette.error.main}`
    : selected
    ? `inset 0 0 0 ${theme.spacing(0.375)} ${theme.palette.primary.main}`
    : `inset 0 0 0 ${theme.spacing(0.125)} ${theme.palette.divider}`,
  backgroundColor: theme.palette.surfaceContainer.lowest,
  boxSizing: "border-box",
  "&:hover": {
    boxShadow: hasError
      ? `inset 0 0 0 ${theme.spacing(0.25)} ${theme.palette.error.main}`
      : `inset 0 0 0 ${selected ? theme.spacing(0.375) : theme.spacing(0.125)} ${theme.palette.primary.main}`,
  },
  "&.Mui-disabled": {
    opacity: theme.palette.action.disabledOpacity,
  },
}));

const TileImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "layout",
})<{ layout: "fill" | "compact" }>(({ layout, theme }) => ({
  width: "100%",
  aspectRatio: layout === "compact" ? "4 / 3" : "59 / 25",
  objectFit: "contain",
  backgroundColor: theme.palette.surfaceContainer.low,
}));

const SelectionIcon = styled(CheckCircleRoundedIcon)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1.5),
  right: theme.spacing(1.5),
  color: theme.palette.primary.main,
  fontSize: theme.spacing(2.5),
}));

export const TileOption = ({
  option,
  selected,
  hasError,
  disabled,
  layout,
  standardLabel,
  onSelect,
}: TileOptionProps) => (
  <TileOptionButton
    component="div"
    role="radio"
    aria-checked={selected}
    selected={selected}
    hasError={hasError}
    disabled={disabled || option.disabled}
    onClick={() => onSelect(option.value)}
  >
    {option.imageUrl ? <TileImage src={option.imageUrl} alt="" layout={layout} /> : null}
    <Stack gap={0.5} sx={{ width: "100%" }}>
      <ControlOptionLabel option={option} standardLabel={standardLabel} emphasis={Boolean(option.imageUrl)} />
      {option.description ? (
        <Typography variant="bodyMedium" color="textSecondary">
          {option.description}
        </Typography>
      ) : null}
    </Stack>
    {selected ? <SelectionIcon aria-hidden /> : null}
  </TileOptionButton>
);
